import React, { useState, useEffect, useContext } from 'react';
import ButtonV2 from 'components/common/ButtonV2';
import { axiosGetAPI, axiosUpdate, axiosPost } from 'services';
import { authContext } from 'contexts/AuthContext';
import { toast } from 'react-toastify';
import { calculateDistanceAndTransit } from './distanceService';

/* ------------------------------------------------------------------ */
/* Shared styles — same as RequestDetailView                          */
/* ------------------------------------------------------------------ */
const headerStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 700,
  color: '#344054',
  background: '#f2f4f7',
  borderBottom: '2px solid #e4e7ec',
  borderRight: '1px solid #e4e7ec',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};

const cellStyle = {
  padding: '12px 14px',
  color: '#344054',
  verticalAlign: 'middle',
  fontSize: '13px',
  borderBottom: '1px solid #e9ecef',
  borderRight: '1px solid #e9ecef',
};

const inputStyle = {
  height: '34px',
  border: '1px solid #d0d5dd',
  borderRadius: '6px',
  padding: '0 10px',
  fontSize: '13px',
  color: '#101828',
  width: '100%',
  outline: 'none',
  background: '#fff',
};

const STATUS_STYLE = {
  Pending: { background: '#fafafa', color: '#475467', border: '1px solid #d0d5dd' },
  Approved: { background: '#ecfdf3', color: '#027a48', border: '1px solid #a6f4c5' },
};

const extractRouteRows = (res) => {
  const payload = res?.data || res;
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const findMatchingRoute = (rows, originId, destId, originName, destName) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  if (originId && destId) {
    const matchById = rows.find(
      (r) =>
        String(r.originCityId ?? r.originCity?.id ?? r.originId ?? '') === String(originId) &&
        String(r.destinationCityId ?? r.destinationCity?.id ?? r.destinationId ?? '') === String(destId)
    );
    if (matchById) return matchById;
  }

  if (originName && destName) {
    const cleanOrigin = String(originName).trim().toLowerCase();
    const cleanDest = String(destName).trim().toLowerCase();
    const matchByName = rows.find(
      (r) =>
        String(r.originCityName ?? r.originCity?.cityName ?? '').trim().toLowerCase() === cleanOrigin &&
        String(r.destinationCityName ?? r.destinationCity?.cityName ?? '').trim().toLowerCase() === cleanDest
    );
    if (matchByName) return matchByName;
  }

  return null;
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const MatrixDetailView = ({ origin, dest, originCityId, destinationCityId, routeId: initialRouteId, vehicleType, onBack }) => {
  const { auth } = useContext(authContext);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [routeId, setRouteId] = useState(initialRouteId || null);

  const [distance, setDistance] = useState('');
  const [transitTime, setTransitTime] = useState('');
  const [rowData, setRowData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchAll = async () => {
      try {
        setLoadingVehicles(true);
        const requests = [
          axiosGetAPI(`${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/vehicle-sizes`, auth),
        ];
        if (initialRouteId) {
          requests.push(axiosGetAPI(`${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/route/${initialRouteId}`, auth));
        }

        const [vehiclesRes, routeRes] = await Promise.all(requests);

        if (isMounted && vehiclesRes?.isSuccessful && vehiclesRes?.data?.data) {
          const apiVehicles = vehiclesRes.data.data.map((v) => ({
            id: v.value,
            name: v.label,
            capacity: `${v.tonnage} Ton`,
          }));
          setVehicles(apiVehicles);

          let currentDist = '';
          let currentTransit = '';
          const rawRoute = routeRes?.data?.data;
          const routeData = rawRoute?.route || rawRoute;
          let matchingRoute = null;

          let rId = routeData?.id || rawRoute?.id || initialRouteId || null;

          // If routeId is not passed or found, look it up by city IDs / names
          if (!rId && (originCityId || origin) && (destinationCityId || dest)) {
            try {
              const apiBaseUrl =
                process.env.REACT_APP_API_BASE_URL ||
                process.env.REACT_APP_REST_API_ENDPOINT?.replace(/\/$/, '');
              let queryParam = 'pageNumber=0&pageSize=100';
              if (originCityId) queryParam += `&originCityId=${originCityId}`;
              if (destinationCityId) queryParam += `&destinationCityId=${destinationCityId}`;
              const routesRes = await axiosGetAPI(
                `${apiBaseUrl}/api/delivery-rate/v2/routes?${queryParam}`,
                auth
              );
              const foundRows = extractRouteRows(routesRes);
              matchingRoute = findMatchingRoute(foundRows, originCityId, destinationCityId, origin, dest);
              if (matchingRoute?.id || matchingRoute?.routeId) {
                rId = matchingRoute.id || matchingRoute.routeId;
              }
            } catch (e) {
              console.error('Error prefetching route in MatrixDetailView:', e);
            }
          }

          if (rId) setRouteId(rId);

          const effectiveRoute = routeData || rawRoute || matchingRoute;

          if (effectiveRoute) {
            const distVal = effectiveRoute?.distanceKm;
            if (distVal !== null && distVal !== undefined && distVal !== '' && Number(distVal) > 0) {
              currentDist = String(distVal);
            }
            const transitVal = effectiveRoute?.transitTimeDays;
            if (transitVal !== null && transitVal !== undefined && transitVal !== '') {
              currentTransit = String(transitVal);
            }
          }

          // If distance is NOT in backend, automatically autofetch exact highway distance
          if (
            (!currentDist || Number(currentDist) === 0) &&
            (origin || effectiveRoute?.originCityName) &&
            (dest || effectiveRoute?.destinationCityName)
          ) {
            try {
              const originStr = origin || effectiveRoute?.originCityName;
              const destStr = dest || effectiveRoute?.destinationCityName;
              const calculated = await calculateDistanceAndTransit(originStr, destStr);
              if (calculated?.distanceKm) {
                currentDist = String(calculated.distanceKm);
              }
            } catch (err) {
              console.warn('Auto-fetch distance failed in MatrixDetailView:', err);
            }
          }

          if (isMounted) {
            setDistance(currentDist);
            setTransitTime(currentTransit);
          }

          setRowData(
            Object.fromEntries(
              apiVehicles.map((v) => {
                const num = v.id.replace('MT_', '');
                const perKgKey = `perKg${num}Mt`;
                const ftlKey = `ftl${num}Mt`;
                let rateVal = '';
                let amountVal = '';

                const rawRate = effectiveRoute?.[perKgKey];
                if (rawRate !== undefined && rawRate !== null && rawRate !== '') {
                  rateVal = String(rawRate);
                }

                // Load ftl (amount) from API if available
                const rawFtl = effectiveRoute?.[ftlKey];
                if (rawFtl !== undefined && rawFtl !== null && rawFtl !== '') {
                  amountVal = String(rawFtl);
                } else if (rateVal) {
                  // Fallback: Calculate amount from rate if ftl not available
                  const tonnage = parseInt(num, 10) || 10;
                  amountVal = String(Math.round(parseFloat(rateVal) * tonnage * 1000));
                }

                return [
                  v.id,
                  {
                    amount: amountVal,
                    rate: rateVal,
                    hasExisting: !!rateVal,
                  },
                ];
              })
            )
          );
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        if (isMounted) setLoadingVehicles(false);
      }
    };
    fetchAll();
    return () => { isMounted = false; };
  }, [auth, origin, dest, initialRouteId]);

  // Entering Rate multiplies by tonnage * 1000 to calculate TotalAmount
  const handleRateChange = (vehicleId, value) => {
    setRowData((prev) => {
      const tonnage = parseInt(vehicleId.replace(/\D/g, ''), 10) || 10;
      const multiplier = tonnage * 1000;
      const calculatedAmount =
        value !== '' && !Number.isNaN(Number(value)) && multiplier
          ? Math.round(parseFloat(value) * multiplier)
          : '';

      return {
        ...prev,
        [vehicleId]: {
          ...prev[vehicleId],
          rate: value,
          amount: calculatedAmount !== '' ? String(calculatedAmount) : '',
        },
      };
    });
  };

  const updateRates = async () => {
    if (!distance || !transitTime) {
      toast.error("Please enter Distance and Transit Time before updating.");
      return;
    }

    const hasAnyRate = Object.values(rowData).some((r) => r.rate && parseFloat(r.rate) > 0);
    if (!hasAnyRate) {
      toast.error("Please enter rate for at least one vehicle.");
      return;
    }

    setSubmitting(true);

    try {
      const parsedTransitDays =
        parseFloat(transitTime) ||
        (transitTime && String(transitTime).match(/\d+(\.\d+)?/) ? parseFloat(String(transitTime).match(/\d+(\.\d+)?/)[0]) : 1);
      const parsedDistance =
        parseFloat(distance) ||
        (distance && String(distance).match(/\d+(\.\d+)?/) ? parseFloat(String(distance).match(/\d+(\.\d+)?/)[0]) : 0);

      // Construct payload according to API specifications:
      // Entries without input must be sent as null
      const payload = {
        distanceKm: parsedDistance,
        transitTimeDays: parsedTransitDays,
      };

      const tonnages = ['10', '15', '20', '25', '30', '35'];
      if (Array.isArray(vehicles)) {
        vehicles.forEach((v) => {
          const num = v.id ? String(v.id).replace(/\D/g, '') : '';
          if (num && !tonnages.includes(num)) {
            tonnages.push(num);
          }
        });
      }

      tonnages.forEach((num) => {
        const ftlKey = `ftl${num}Mt`;
        const perKgKey = `perKg${num}Mt`;
        const row =
          rowData[`MT_${num}`] ||
          rowData[num] ||
          Object.entries(rowData).find(([k]) => k.replace(/\D/g, '') === num)?.[1];

        const hasRate =
          row &&
          row.rate !== '' &&
          row.rate !== null &&
          row.rate !== undefined &&
          !Number.isNaN(Number(row.rate)) &&
          parseFloat(row.rate) > 0;

        if (hasRate) {
          const rateVal = parseFloat(row.rate);
          let amountVal = null;
          if (row.amount !== '' && row.amount !== null && row.amount !== undefined && !Number.isNaN(Number(row.amount))) {
            amountVal = parseFloat(row.amount);
          } else {
            const tonInt = parseInt(num, 10) || 10;
            amountVal = Math.round(rateVal * tonInt * 1000);
          }
          payload[ftlKey] = amountVal;
          payload[perKgKey] = rateVal;
        } else {
          payload[ftlKey] = null;
          payload[perKgKey] = null;
        }
      });

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_REST_API_ENDPOINT?.replace(/\/$/, '');

      let updateSuccess = false;
      let successMsg = "Delivery route updated successfully.";
      let lastErrMsg = "";

      // Resolve route ID if not available in state
      let targetRouteId = routeId;
      if (!targetRouteId && (originCityId || origin) && (destinationCityId || dest)) {
        try {
          let queryParam = 'pageNumber=0&pageSize=100';
          if (originCityId) queryParam += `&originCityId=${originCityId}`;
          if (destinationCityId) queryParam += `&destinationCityId=${destinationCityId}`;
          const routesRes = await axiosGetAPI(
            `${apiBaseUrl}/api/delivery-rate/v2/routes?${queryParam}`,
            auth
          );
          const foundRows = extractRouteRows(routesRes);
          const matchingRoute = findMatchingRoute(foundRows, originCityId, destinationCityId, origin, dest);
          if (matchingRoute?.id || matchingRoute?.routeId) {
            targetRouteId = matchingRoute.id || matchingRoute.routeId;
            setRouteId(targetRouteId);
          }
        } catch (e) {
          console.error('Error resolving route ID for matrix update:', e);
        }
      }

      // 1. If targetRouteId exists, update via PUT on route
      if (targetRouteId) {
        const url = `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/route/${targetRouteId}`;
        const response = await axiosUpdate(url, payload, auth?.data?.token);
        if (response?.isSuccessful) {
          updateSuccess = true;
          successMsg = response?.data?.message || successMsg;
        } else {
          lastErrMsg = response?.message || response?.data?.message || "";
        }
      } else {
        lastErrMsg = "Route ID not found to update. Please select a valid route.";
      }

      if (updateSuccess) {
        setRowData((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((id) => {
            if (next[id].rate) {
              next[id] = { ...next[id], hasExisting: true };
            }
          });
          return next;
        });
        toast.success(successMsg);
        if (onBack) {
          onBack();
        }
      } else {
        toast.error(lastErrMsg || "Failed to update rates.");
      }
    } catch (err) {
      console.error("Error updating rates:", err);
      toast.error("An error occurred while updating rates.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-wrap">
      {/* Origin / Destination / Distance / Transit Time */}
      <div className="sr-filter-card">
        <div className="mx-filter-row">

          <div className="mx-field">
            <label className="sr-label" htmlFor="mxd-origin">Origin</label>
            <input
              id="mxd-origin"
              type="text"
              className="sr-select"
              style={{ height: '42px', backgroundColor: '#fff', cursor: 'default' }}
              value={origin || ''}
              readOnly
            />
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="mxd-dest">Destination</label>
            <input
              id="mxd-dest"
              type="text"
              className="sr-select"
              style={{ height: '42px', backgroundColor: '#fff', cursor: 'default' }}
              value={dest || ''}
              readOnly
            />
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="mxd-distance">Distance (km)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                id="mxd-distance"
                type="number"
                min="0"
                placeholder="e.g. 1400"
                className="sr-select"
                style={{ height: '42px' }}
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
              />
              <span style={{ color: '#667085', fontSize: '13px', whiteSpace: 'nowrap' }}>km</span>
            </div>
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="mxd-transit">Transit Time</label>
            <input
              id="mxd-transit"
              type="text"
              placeholder="e.g. 3 Days"
              className="sr-select"
              style={{ height: '42px' }}
              value={transitTime}
              onChange={(e) => setTransitTime(e.target.value)}
            />
          </div>

        </div>
      </div>

      {/* Vehicle rate table */}
      <div className="push-notification-container" style={{ padding: 0, gap: 0 }}>

        {/* Title */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e4e7ec', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#1d2939' }}>
            Enter Rate per Vehicle
          </span>
          <span style={{ fontSize: '12px', color: '#667085' }}>₹ / kg</span>
          <span style={{ fontSize: '11px', color: '#98a2b3', marginLeft: 4 }}>
            — Pre-filled rates are from existing matrix data
          </span>
        </div>

        {/* Table */}
        <div className="push-notification__table-wrap" style={{ borderRadius: 0, border: 0 }}>
          <table className="push-notification__table" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, width: 50 }}>S.No.</th>
                <th style={headerStyle}>Vehicle Type</th>
                <th style={{ ...headerStyle, width: 110 }}>Capacity</th>
                <th style={{ ...headerStyle, width: 110 }}>Status</th>
                <th style={{ ...headerStyle, width: 180 }}>Rate+Loading (₹/kg)</th>
                <th style={{ ...headerStyle, width: 180 }}>TotalAmount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loadingVehicles ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>Loading vehicle types...</td>
                </tr>
              ) : vehicles.map((vehicle, idx) => {
                const row = rowData[vehicle.id];
                if (!row) return null;
                const isSelected = vehicle.id === vehicleType;
                const statusKey = row.hasExisting ? 'Approved' : 'Pending';
                let rowBackground = idx % 2 === 0 ? '#fff' : '#fafafa';
                if (isSelected) rowBackground = '#f5f8ff';

                return (
                  <tr key={vehicle.id} style={{ background: rowBackground }}>
                    {/* S.No. */}
                    <td style={{ ...cellStyle, color: '#667085', textAlign: 'center' }}>{idx + 1}</td>

                    {/* Vehicle name */}
                    <td style={{ ...cellStyle, fontWeight: 600, color: '#1d2939' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {vehicle.name}
                        {isSelected && (
                          <span style={{ fontSize: '10px', fontWeight: 700, background: '#eff4ff', color: '#1d4ed8', border: '1px solid #c7d7fe', borderRadius: '999px', padding: '2px 8px' }}>
                            Selected
                          </span>
                        )}
                        {row.hasExisting && !isSelected && (
                          <span style={{ fontSize: '10px', fontWeight: 700, background: '#f2f4f7', color: '#667085', border: '1px solid #e4e7ec', borderRadius: '999px', padding: '2px 8px' }}>
                            Existing
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Capacity */}
                    <td style={cellStyle}>
                      <span className="sr-trip-vehicle" style={{ fontSize: '12px' }}>{vehicle.capacity}</span>
                    </td>

                    {/* Status */}
                    <td style={cellStyle}>
                      <span style={{ display: 'inline-block', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, ...(STATUS_STYLE[statusKey]) }}>
                        {statusKey}
                      </span>
                    </td>

                    {/* Rate+Loading input (editable, triggers amount multiplication) */}
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="e.g. 10.50"
                          aria-label={`Rate for ${vehicle.name}`}
                          style={{ ...inputStyle, background: '#fff' }}
                          value={row.rate || ''}
                          onChange={(e) => handleRateChange(vehicle.id, e.target.value)}
                        />
                        <span style={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>/kg</span>
                      </div>
                    </td>

                    {/* TotalAmount (auto-calculated from rate * tonnage * 1000, readonly) */}
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="Auto"
                          aria-label={`Amount for ${vehicle.name}`}
                          style={{ ...inputStyle, background: '#f8fafc' }}
                          value={row.amount || ''}
                          readOnly
                        />
                      </div>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer: Update All */}
        <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e4e7ec' }}>
          <ButtonV2
            color="primary"
            style={{ height: '40px', padding: '0 24px', fontSize: '14px', fontWeight: 700, borderRadius: '8px' }}
            disabled={submitting || Object.values(rowData).every(r => !r.rate) || !distance || !transitTime}
            onClick={() => updateRates()}
          >
            {submitting ? 'Updating...' : 'Update All'}
          </ButtonV2>
        </div>

      </div>
    </div>
  );
};

export default MatrixDetailView;
